"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

export default function EditProductPage () {
    const [categories, setCategories] = useState([]);
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [productSpecifications, setProductSpecifications] = useState([]); // Changed to match new system
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSpecIndex, setEditingSpecIndex] = useState(null);
    const [modalFormData, setModalFormData] = useState({});
    const [deleteModal, setDeleteModal] = useState({
        show: false,
        type: 'table', // 'table' or 'spec'
        tableIndex: null,
        specId: null
    });
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const specificationFields = {
        size: {
            title: "Size Specifications",
            fields: [
                { name: "size_length1", label: "Length 1", type: "text", required: false },
                { name: "size_width1", label: "Width 1", type: "text", required: false },
                { name: "size_length2", label: "Length 2", type: "text", required: false },
                { name: "size_width2", label: "Width 2", type: "text", required: false },
            ],
        },
        thickness: {
            title: "Thickness Options",
            fields: [
                { name: "thickness_option1", label: "Thickness Option 1", type: "text", required: false },
                { name: "thickness_option2", label: "Thickness Option 2", type: "text", required: false },
            ],
        },
        underpad: {
            title: "Underpad Configuration",
            fields: [
                { name: "underpad_thickness", label: "Underpad Thickness", type: "text", required: false },
            ],
        },
        wearResistant: {
            title: "Wear Resistant Layer",
            fields: [
                { name: "wear_layer1", label: "Layer 1 (mil)", type: "text", required: false },
                { name: "wear_layer2", label: "Layer 2 (mil)", type: "text", required: false },
            ],
        },
         price: {
            title: "Price",
            fields: [
                { name: "Price", label: "Price", type: "number", required: false },
            ],
        },
         inventory: {
            title: "Inventory",
            fields: [
                { name: "Inventory", label: "Inventory", type: "number", required: false },
            ],
        },
    };

    const [formData, setFormData] = useState({
        product_name: "",
        description: "",
        short_description: "",
        price: "",
        inventory: "",
        category_id: "",
        images: [],
    });

    useEffect(() => {
        if (id) {
            fetchProductData(id);
        } else {
            setLoading(false);
            toast.error("Product ID is missing");
            navigate("/dashboard/products");
        }
    }, [id, navigate]);

    const fetchProductData = async (productId) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch("https://leapatinternational.com/admin/api/v1/product.php");
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const result = await response.json();
            if (result.status === "success" && result.data) {
                const product = result.data.find(p => p.id === productId);
                if (product) {
                    // Parse existing specifications and convert to new format
                    let parsedSpecs = [];
                    if (product.specifications) {
                        try {
                            const oldSpecs = JSON.parse(product.specifications);
                            // Convert old format to new format (separate tables)
                            if (Array.isArray(oldSpecs) && oldSpecs.length > 0) {
                                // Check if it's already in new format (array of objects with product1, product2, etc.)
                                if (oldSpecs[0].product1 || oldSpecs[0].product2) {
                                    // New format - convert to internal format
                                    parsedSpecs = oldSpecs.map(productSpec => {
                                        const productKey = Object.keys(productSpec)[0]; // product1, product2, etc.
                                        const specs = productSpec[productKey];
                                        const specArray = [];

                                        Object.keys(specs).forEach(specType => {
                                            specArray.push({
                                                id: Date.now() + Math.random(),
                                                type: specType === 'wear-resistant' ? 'wear-resistant' : specType,
                                                data: specs[specType]
                                            });
                                        });

                                        return specArray;
                                    });
                                } else {
                                    // Old format - convert each spec to a separate table
                                    parsedSpecs = oldSpecs.map(spec => [{
                                        id: Date.now() + Math.random(),
                                        type: spec.type,
                                        data: spec.data
                                    }]);
                                }
                            }
                        } catch (e) {
                            console.error("Error parsing specifications:", e);
                            parsedSpecs = [];
                        }
                    }

                    setFormData(prev => ({
                        ...prev,
                        product_name: product.product_name || "",
                        description: product.description || "",
                        short_description: product.short_description || "",
                        price: product.price || "",
                        inventory: product.inventory || "",
                        category_id: product.category_id || "",
                        images: product.image_gallery || [],
                    }));

                    setProductSpecifications(parsedSpecs);

                    const existingPreviews = product?.image_gallery?.map((url, index) => ({
                        id: `existing-${index}`,
                        url,
                        name: `Existing Image ${index + 1}`,
                        isExisting: true,
                    })) || [];
                    setImagePreviews(existingPreviews);
                } else {
                    throw new Error(`Product with ID ${productId} not found`);
                }
            } else {
                throw new Error(result.message || "Failed to fetch products");
            }
        } catch (err) {
            console.error("Error fetching product:", err);
            setError(err.message || "Failed to load product data");
            toast.error(err.message || "Failed to load product data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (formData.images.length + files.length > 10) {
            toast.error("You can upload a maximum of 10 images.");
            return;
        }

        const newImages = [];
        const newPreviews = [];
        let validFilesCount = 0;

        files.forEach((file, index) => {
            const isDuplicate = formData.images.some(img =>
                typeof img !== "string" && img.name === file.name && img.file.size === file.size
            );
            if (isDuplicate) {
                toast.warning(`File "${file.name}" is already selected.`);
                return;
            }
            if (!file.type.startsWith('image/')) {
                toast.error(`File "${file.name}" is not an image.`);
                return;
            }
            const imageId = Date.now() + index + Math.random();
            newImages.push({ id: imageId, file: file, name: file.name });
            newPreviews.push({ id: imageId, url: URL.createObjectURL(file), name: file.name });
            validFilesCount++;
        });

        if (newImages.length > 0) {
            setFormData(prev => ({ ...prev, images: [...prev.images, ...newImages] }));
            setImagePreviews(prev => [...prev, ...newPreviews]);
            toast.success(`${validFilesCount} image(s) added successfully!`);
        }
        e.target.value = '';
    };

    const handleRemoveImage = (imageId) => {
        const imageToRemove = imagePreviews.find(img => img.id === imageId);
        if (!imageToRemove) return;

        setFormData(prev => ({
            ...prev,
            images: prev.images.filter(img => {
                if (imageToRemove.isExisting) return img !== imageToRemove.url;
                return typeof img === "string" ? true : img.id !== imageId;
            })
        }));

        if (!imageToRemove.isExisting) {
            URL.revokeObjectURL(imageToRemove.url);
        }
        setImagePreviews(prev => prev.filter(img => img.id !== imageId));
    };

    const handleRemoveAllImages = () => {
        imagePreviews.forEach(preview => {
            if (!preview.isExisting) URL.revokeObjectURL(preview.url);
        });
        setFormData(prev => ({ ...prev, images: [] }));
        setImagePreviews([]);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
    };

    // Modal Functions - Updated for new system
    const openModal = (specIndex = null) => {
        setEditingSpecIndex(specIndex);

        if (specIndex !== null) {
            // If editing, populate the modal with existing spec data
            const existingSpecs = productSpecifications[specIndex];
            const initialData = {};

            // Initialize all fields to empty first
            Object.keys(specificationFields).forEach(specType => {
                specificationFields[specType].fields.forEach(field => {
                    initialData[field.name] = "";
                });
            });

            // Populate existing data
            existingSpecs.forEach(spec => {
                if (spec.type === "size") {
                    initialData.size_length1 = spec.data.length1 || "";
                    initialData.size_width1 = spec.data.width1 || "";
                    initialData.size_length2 = spec.data.length2 || "";
                    initialData.size_width2 = spec.data.width2 || "";
                } else if (spec.type === "thickness") {
                    initialData.thickness_option1 = spec.data.option1 || "";
                    initialData.thickness_option2 = spec.data.option2 || "";
                } else if (spec.type === "underpad") {
                    initialData.underpad_thickness = spec.data.thickness || "";
                } else if (spec.type === "wear-resistant") {
                    initialData.wear_layer1 = spec.data.layer1 || "";
                    initialData.wear_layer2 = spec.data.layer2 || "";
                } else if (spec.type === "price") {
                    initialData.Price = spec.data.Price || "";
                } else if (spec.type === "inventory") {
                    initialData.Inventory = spec.data.Inventory || "";
                }
            });

            setModalFormData(initialData);
        } else {
            // Initialize empty form data for all fields
            const initialData = {};
            Object.keys(specificationFields).forEach(specType => {
                specificationFields[specType].fields.forEach(field => {
                    initialData[field.name] = "";
                });
            });
            setModalFormData(initialData);
        }

        setIsModalOpen(true);
    };

    const handleModalInputChange = (e) => {
        const { name, value } = e.target;
        setModalFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleModalSubmit = () => {
        // Collect all filled specifications
        const addedSpecs = [];

        // Check Size specifications
        const sizeData = {
            length1: modalFormData.size_length1,
            width1: modalFormData.size_width1,
            length2: modalFormData.size_length2,
            width2: modalFormData.size_width2,
        };
        if (Object.values(sizeData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random(),
                type: "size",
                data: sizeData
            });
        }

        // Check Thickness specifications
        const thicknessData = {
            option1: modalFormData.thickness_option1,
            option2: modalFormData.thickness_option2,
        };
        if (Object.values(thicknessData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 1,
                type: "thickness",
                data: thicknessData
            });
        }

        // Check Underpad specifications
        const underpadData = {
            thickness: modalFormData.underpad_thickness,
        };
        if (Object.values(underpadData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 2,
                type: "underpad",
                data: underpadData
            });
        }

        // Check Wear Resistant specifications
        const wearData = {
            layer1: modalFormData.wear_layer1,
            layer2: modalFormData.wear_layer2,
        };
        if (Object.values(wearData).some(val => val?.trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 3,
                type: "wear-resistant",
                data: wearData  
            });
        }

        // Check Price
        const priceData = {
            Price: modalFormData.Price,
        };
        if (Object.values(priceData).some(val => val?.toString().trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 4,
                type: "price",
                data: priceData
            });
        }

        // Check Inventory
        const inventoryData = {
            Inventory: modalFormData.Inventory,
        };
        if (Object.values(inventoryData).some(val => val?.toString().trim())) {
            addedSpecs.push({
                id: Date.now() + Math.random() + 5,
                type: "inventory",
                data: inventoryData
            });
        }

        if (addedSpecs.length === 0) {
            toast.error("Please fill at least one specification field.");
            return;
        }

        if (editingSpecIndex !== null) {
            // If editing, update existing specification set
            setProductSpecifications(prev => {
                const updated = [...prev];
                updated[editingSpecIndex] = addedSpecs;
                return updated;
            });
            toast.success("Specification table updated successfully!");
        } else {
            // If adding new, create a new specification set (new table)
            setProductSpecifications(prev => [...prev, addedSpecs]);
            toast.success("New specification table added successfully!");
        }

        setIsModalOpen(false);
        setEditingSpecIndex(null);
        setModalFormData({});
    };

    // Delete Functions - Support both table and individual spec deletion
    const openDeleteModal = (type, tableIndex, specId = null) => {
        setDeleteModal({
            show: true,
            type,
            tableIndex,
            specId
        });
    };

    const confirmDelete = () => {
        if (deleteModal.type === 'table') {
            // Delete entire table
            setProductSpecifications(prev => prev.filter((_, index) => index !== deleteModal.tableIndex));
            toast.success("Specification table deleted successfully!");
        } else if (deleteModal.type === 'spec') {
            // Delete individual specification
            setProductSpecifications(prev => {
                const updated = [...prev];
                updated[deleteModal.tableIndex] = updated[deleteModal.tableIndex].filter(spec => spec.id !== deleteModal.specId);

                // If table becomes empty after deleting spec, remove the entire table
                if (updated[deleteModal.tableIndex].length === 0) {
                    updated.splice(deleteModal.tableIndex, 1);
                    toast.success("Specification deleted successfully! Table was removed as it became empty.");
                } else {
                    toast.success("Specification deleted successfully!");
                }

                return updated;
            });
        }

        setDeleteModal({ show: false, type: 'table', tableIndex: null, specId: null });
    };

    const cancelDelete = () => {
        setDeleteModal({ show: false, type: 'table', tableIndex: null, specId: null });
    };

    const getSpecTitle = (type) => {
        switch (type) {
            case "size":
                return "Size Specifications";
            case "thickness":
                return "Thickness Options";
            case "underpad":
                return "Underpad Configuration";
            case "wear-resistant":
                return "Wear Resistant Layer";
            case "price":
                return "Price";
            case "inventory":
                return "Inventory";
            default:
                return type;
        }
    };

    const getSpecValues = (spec) => {
        switch (spec.type) {
            case "size":
                return Object.values(spec.data).filter(val => val?.trim());
            case "thickness":
                return Object.values(spec.data).filter(val => val?.trim());
            case "underpad":
                return Object.values(spec.data).filter(val => val?.trim());
            case "wear-resistant":
                return Object.values(spec.data).filter(val => val?.trim());
            case "price":
                return Object.values(spec.data).filter(val => val?.toString().trim());
            case "inventory":
                return Object.values(spec.data).filter(val => val?.toString().trim());
            default:
                return [];
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const data = new FormData();
            data.append("id", id);
            data.append("product_name", formData.product_name);
            data.append("description", formData.description);
            data.append("short_description", formData.short_description);
            data.append("price", formData.price);
            data.append("inventory", formData.inventory);
            data.append("category_id", formData.category_id);

            // Format specifications as requested: specifications: [product1: {}, product2: {}]
            const formattedSpecifications = productSpecifications.map((specSet, index) => {
                const productSpecs = {};
                specSet.forEach(spec => {
                    productSpecs[spec.type] = spec.data;
                });
                return { [`product${index + 1}`]: productSpecs };
            });

            data.append("specifications", JSON.stringify(formattedSpecifications));

            const existingImageUrls = formData.images.filter(image => typeof image === 'string');
            const newImageFiles = formData.images.filter(image => typeof image !== 'string');

            data.append('existing_images', JSON.stringify(existingImageUrls));

            newImageFiles.forEach((imageObject, index) => {
                data.append(`new_images[${index}]`, imageObject.file);
            });

            const endpoint = "https://leapatinternational.com/admin/api/v1/product_edit.php";
            const response = await fetch(endpoint, { method: "POST", body: data });
            const result = await response.json();

            if (result.ok === true || result.status === "success") {
                toast.success("Product updated successfully!");
                navigate('/dashboard/products');
            } else {
                toast.error(result.message || "Failed to update product");
            }
        } catch (err) {
            console.error("Error:", err);
            toast.error("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch("https://leapatinternational.com/admin/api/v1/category.php");
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                const result = await response.json();
                setCategories(result.data || []);
            } catch (error) {
                console.error("Error fetching categories:", error);
                toast.error("Failed to load categories");
                setCategories([]);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        return () => {
            imagePreviews.forEach(preview => {
                if (preview.url && preview.url.startsWith('blob:') && !preview.isExisting) {
                    URL.revokeObjectURL(preview.url);
                }
            });
        };
    }, [imagePreviews]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCancel = () => navigate('/dashboard/products');

    if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (error) return <div className="alert alert-danger" role="alert"><h4 className="alert-heading">Error Loading Product</h4><p>{error}</p><button className="back-button" onClick={handleCancel}>← Back to Products</button></div>;

    return (
        <div className="category-management-section">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <h1 className="page-title mb-0">Edit Product</h1>
                            <p className="page-subtitle mb-0">Update product information</p>
                        </div>
                        <button className="back-button me-3" onClick={handleCancel}>
                            ← Back to Products
                        </button>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header"><h5 className="card-title mb-0">Product Information</h5></div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Left Column */}
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Product Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="product_name"
                                                value={formData.product_name}
                                                onChange={handleInputChange}
                                                placeholder="Enter product name"
                                                required
                                            />
                                            <div className="form-text">Choose a clear, descriptive name for your product</div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="form-label">Category *</label>
                                            <select
                                                className="form-select"
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select a category</option>
                                                {categories.map((category) => (
                                                    <option key={category.id} value={category.id}>
                                                        {category.title}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="form-text">Choose the most appropriate category for this product</div>
                                        </div>

                                        {/* <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="form-label">Price *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="price"
                                                        value={formData.price}
                                                        onChange={handleInputChange}
                                                        placeholder="0.00"
                                                        min="0"
                                                        step="0.01"
                                                        required
                                                    />
                                                    <div className="form-text">Enter product price</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="form-label">Inventory *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="inventory"
                                                        value={formData.inventory}
                                                        onChange={handleInputChange}
                                                        placeholder="0"
                                                        min="0"
                                                        required
                                                    />
                                                    <div className="form-text">Available stock quantity</div>
                                                </div>
                                            </div>
                                        </div> */}
                                    </div>

                                    {/* Right Column */}
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label className="form-label mb-0">Product Images Gallery</label>
                                                {imagePreviews.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={handleRemoveAllImages}
                                                    >
                                                        Remove All
                                                    </button>
                                                )}
                                            </div>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                multiple
                                            />
                                            <div className="form-text">
                                                Upload multiple product images (JPG, PNG, GIF). Maximum 10 images, 5MB each.
                                                <br />
                                                <small className="text-muted">
                                                    {formData.images.length}/10 images selected
                                                </small>
                                                <br />
                                                <small className="text-info">
                                                    💡 You can select images multiple times to add more to your gallery
                                                </small>
                                            </div>

                                            {/* Image Gallery Preview */}
                                            {imagePreviews.length > 0 && (
                                                <div className="mt-3">
                                                    <label className="form-label">Image Gallery Preview</label>
                                                    <div className="row g-2">
                                                        {imagePreviews.map((preview) => (
                                                            <div key={preview.id} className="col-6 col-md-4">
                                                                <div className="position-relative">
                                                                    <img
                                                                        src={preview.url}
                                                                        alt={preview.name}
                                                                        className="img-thumbnail w-100"
                                                                        style={{
                                                                            height: "120px",
                                                                            objectFit: "cover",
                                                                            cursor: "pointer"
                                                                        }}
                                                                        onClick={() => {
                                                                            window.open(preview.url, '_blank');
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                                                                        onClick={() => handleRemoveImage(preview.id)}
                                                                        style={{
                                                                            width: "24px",
                                                                            height: "24px",
                                                                            padding: "0",
                                                                            fontSize: "12px",
                                                                            lineHeight: "1"
                                                                        }}
                                                                        title="Remove image"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                    <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white p-1">
                                                                        <small className="text-truncate d-block" style={{ fontSize: "10px" }}>
                                                                            {preview.name}
                                                                            {preview.isExisting && <span className="text-warning"> (Existing)</span>}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Full Width Fields */}
                                <div className="mb-4">
                                    <label className="form-label">Short Description</label>
                                    <textarea
                                        className="form-control"
                                        name="short_description"
                                        value={formData.short_description}
                                        onChange={handleInputChange}
                                        rows="2"
                                        placeholder="Enter a brief product summary"
                                    ></textarea>
                                    <div className="form-text">Brief summary of the product (optional)</div>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label">Product Description</label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="5"
                                        placeholder="Enter detailed product description"
                                    ></textarea>
                                    <div className="form-text">Provide detailed information about the product features and benefits</div>
                                </div>

                                {/* Enhanced Product Specifications Section */}
                                <div style={{
                                    background: '#fff',
                                    padding: '20px',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <div>
                                            <h2 style={{ margin: '0 0 5px 0' }}>Product Specifications</h2>
                                           
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => openModal()}
                                            className="add-button"
                                        >
                                            + Add New Specification
                                        </button>
                                    </div>

                                    {/* Display Multiple Specification Tables with Individual Delete Buttons */}
                                    {productSpecifications.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                            {productSpecifications.map((specSet, tableIndex) => (
                                                <div key={tableIndex} style={{
                                                    border: '2px solid #e3f2fd',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden'
                                                }}>
                                                    {/* Table Header */}
                                                    <div style={{
                                                        // background: '#494EA1',
                                                        color: 'white',
                                                        padding: '7px 20px',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}>
                                                        <h3 style={{ margin: '0', fontSize: '18px', color: "#fff" }}>
                                                            {/* 📋 Specification Product {tableIndex + 1} */}
                                                        </h3>
                                                        <div style={{ display: 'flex', gap: '10px' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => openModal(tableIndex)}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    background: '#494EA1',
                                                                    border: '1px solid rgba(255,255,255,0.3)',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    color: 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '5px'
                                                                }}
                                                                title="Edit this specification table"
                                                            >
                                                                ✏️ Edit 
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => openDeleteModal('table', tableIndex)}
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    background: 'rgba(244,67,54,0.8)',
                                                                    border: '1px solid rgba(244,67,54,0.3)',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    color: 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '5px'
                                                                }}
                                                                title="Delete this specification table"
                                                            >
                                                                🗑️ Delete 
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Table Content with Individual Delete Buttons */}
                                                    <div style={{ background: 'white' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                <tr style={{ borderBottom: '2px solid #ddd', background: '#f8f9fa' }}>
                                                                    <th style={{
                                                                        padding: '12px 15px',
                                                                        textAlign: 'left',
                                                                        fontWeight: 'bold',
                                                                        color: '#333'
                                                                    }}>
                                                                        Specification Type
                                                                    </th>
                                                                    <th style={{
                                                                        padding: '12px 15px',
                                                                        textAlign: 'left',
                                                                        fontWeight: 'bold',
                                                                        color: '#333'
                                                                    }}>
                                                                        Values
                                                                    </th>
                                                                    {/* <th style={{
                                                                        padding: '12px 15px',
                                                                        textAlign: 'center',
                                                                        fontWeight: 'bold',
                                                                        color: '#333',
                                                                        width: '100px'
                                                                    }}>
                                                                        Actions
                                                                    </th> */}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {specSet.map((spec, specIndex) => (
                                                                    <tr key={spec.id} style={{
                                                                        borderBottom: '1px solid #eee',
                                                                        backgroundColor: specIndex % 2 === 0 ? '#ffffff' : '#f9f9f9'
                                                                    }}>
                                                                        <td style={{
                                                                            padding: '12px 15px',
                                                                            verticalAlign: 'top'
                                                                        }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                <span style={{ fontWeight: '500', color: '#333' }}>
                                                                                    {getSpecTitle(spec.type)}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td style={{
                                                                            padding: '12px 15px',
                                                                            verticalAlign: 'top',
                                                                            width: '45%'
                                                                        }}>
                                                                            <div style={{
                                                                                display: 'flex',
                                                                                flexWrap: 'wrap',
                                                                                gap: '6px',
                                                                                alignItems: 'center'
                                                                            }}>
                                                                                {getSpecValues(spec).map((value, valueIndex) => (
                                                                                    <span key={valueIndex} style={{
                                                                                        background: '#e3f2fd',
                                                                                        color: '#1976d2',
                                                                                        padding: '4px 8px',
                                                                                        borderRadius: '4px',
                                                                                        fontSize: '13px',
                                                                                        border: '1px solid #bbdefb',
                                                                                        fontWeight: '500'
                                                                                    }}>
                                                                                        {value}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </td>
                                                                        {/* <td style={{
                                                                            padding: '12px 0px',
                                                                            textAlign: 'center', verticalAlign: 'top'
                                                                        }}>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openDeleteModal('spec', tableIndex, spec.id)}
                                                                                style={{
                                                                                    padding: '6px 10px',
                                                                                    background: '#ffebee',
                                                                                    border: '1px solid #ef9a9a',
                                                                                    borderRadius: '4px',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '12px',
                                                                                    color: '#c62828',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '4px',
                                                                                    margin: '0 auto'
                                                                                }}
                                                                                title="Delete this specification"
                                                                            >
                                                                                🗑️ Delete
                                                                            </button>
                                                                        </td> */}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#666', background: '#f8f9fa', borderRadius: '6px', marginTop: '15px' }}>
                                            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📋</div>
                                            <p style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '500' }}>No specification added yet</p>
                                            <p style={{ margin: '0', fontSize: '14px' }}>Click the "Add New Specification " button above to get started.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex gap-2 pt-3 border-top">
                                    <button type="submit" className="add-button" disabled={isSubmitting}>
                                        {isSubmitting ? 'Updating...' : 'Update Product'}
                                    </button>
                                    <button type="button" className="back-button pe-3 ps-3" onClick={handleCancel} disabled={isSubmitting}>
                                        Cancel
                                    </button>
                                </div>
                            </form>

                            {/* Specifications Modal */}
                            {isModalOpen && (
                                <div style={{
                                    position: 'fixed',
                                    top: '0',
                                    left: '0',
                                    right: '0',
                                    bottom: '0',
                                    background: 'rgba(0,0,0,0.5)',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: '1000'
                                }}>
                                    <div style={{
                                        background: 'white',
                                        padding: '25px',
                                        borderRadius: '8px',
                                        width: '100%',
                                        maxWidth: '800px',
                                        maxHeight: '85vh',
                                        overflowY: 'auto'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                                            <h2 style={{ margin: '0', color: '#333' }}>
                                                {editingSpecIndex !== null ? `Edit Specification Product ${editingSpecIndex + 1}` : "Add New Specification Product"}
                                            </h2>
                                            <button
                                                onClick={() => setIsModalOpen(false)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    fontSize: '24px',
                                                    cursor: 'pointer',
                                                    color: '#666'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>

                                    

                                        {/* All Specifications in One Screen */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                            {/* Size Specifications */}
                                            <div style={{
                                                background: '#f8f9fa',
                                                padding: '20px',
                                                borderRadius: '8px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <h3 style={{ margin: '0 0 15px 0', color: '#2196F3', fontSize: '18px' }}>
                                                     Size Specifications
                                                </h3>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                    gap: '15px'
                                                }}>
                                                    {specificationFields.size.fields.map(field => (
                                                        <div key={field.name}>
                                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                                {field.label}
                                                            </label>
                                                            <input
                                                                type={field.type}
                                                                name={field.name}
                                                                value={modalFormData[field.name] || ""}
                                                                onChange={handleModalInputChange}
                                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Thickness Specifications */}
                                            <div style={{
                                                background: '#f8f9fa',
                                                padding: '20px',
                                                borderRadius: '8px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <h3 style={{ margin: '0 0 15px 0', color: '#ff9800', fontSize: '18px' }}>
                                                     Thickness Options
                                                </h3>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                    gap: '15px'
                                                }}>
                                                    {specificationFields.thickness.fields.map(field => (
                                                        <div key={field.name}>
                                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                                {field.label}
                                                            </label>
                                                            <input
                                                                type={field.type}
                                                                name={field.name}
                                                                value={modalFormData[field.name] || ""}
                                                                onChange={handleModalInputChange}
                                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Underpad Specifications */}
                                            <div style={{
                                                background: '#f8f9fa',
                                                padding: '20px',
                                                borderRadius: '8px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <h3 style={{ margin: '0 0 15px 0', color: '#4caf50', fontSize: '18px' }}>
                                                    Underpad Configuration
                                                </h3>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr',
                                                    gap: '15px'
                                                }}>
                                                    {specificationFields.underpad.fields.map(field => (
                                                        <div key={field.name}>
                                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                                {field.label}
                                                            </label>
                                                            <input
                                                                type={field.type}
                                                                name={field.name}
                                                                value={modalFormData[field.name] || ""}
                                                                onChange={handleModalInputChange}
                                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Wear Resistant Specifications */}
                                            <div style={{
                                                background: '#f8f9fa',
                                                padding: '20px',
                                                borderRadius: '8px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <h3 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '18px' }}>
                                                     Wear Resistant Layer
                                                </h3>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                    gap: '15px'
                                                }}>
                                                    {specificationFields.wearResistant.fields.map(field => (
                                                        <div key={field.name}>
                                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                                {field.label}
                                                            </label>
                                                            <input
                                                                type={field.type}
                                                                name={field.name}
                                                                value={modalFormData[field.name] || ""}
                                                                onChange={handleModalInputChange}
                                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                             <div style={{
                                                background: '#f8f9fa',
                                                padding: '20px',
                                                borderRadius: '8px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <h3 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '18px' }}>
                                                     Price *
                                                </h3>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                    gap: '15px'
                                                }}>
                                                    {specificationFields.price.fields.map(field => (
                                                        <div key={field.name}>
                                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                                {field.label}
                                                            </label>
                                                            <input
                                                                type={field.type}
                                                                name={field.name}
                                                                value={modalFormData[field.name] || ""}
                                                                onChange={handleModalInputChange}
                                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                             <div style={{
                                                background: '#f8f9fa',
                                                padding: '20px',
                                                borderRadius: '8px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <h3 style={{ margin: '0 0 15px 0', color: '#9c27b0', fontSize: '18px' }}>
                                                    Inventory *
                                                </h3>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                    gap: '15px'
                                                }}>
                                                    {specificationFields.inventory.fields.map(field => (
                                                        <div key={field.name}>
                                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                                                {field.label}
                                                            </label>
                                                            <input
                                                                type={field.type}
                                                                name={field.name}
                                                                value={modalFormData[field.name] || ""}
                                                                onChange={handleModalInputChange}
                                                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '10px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px'
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px' }}>
                                            <button
                                                type="button"
                                                onClick={() => setIsModalOpen(false)}
                                                className="back-button pe-3 ps-3"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleModalSubmit}
                                                className="add-button"
                                            >
                                                {editingSpecIndex !== null ? "Update Product" : "Add New Product"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Enhanced Delete Confirmation Modal */}
                            {deleteModal.show && (
                                <div style={{
                                    position: 'fixed',
                                    top: '0',
                                    left: '0',
                                    right: '0',
                                    bottom: '0',
                                    background: 'rgba(0,0,0,0.5)',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: '1001'
                                }}>
                                    <div style={{
                                        background: 'white',
                                        padding: '25px',
                                        borderRadius: '8px',
                                        width: '100%',
                                        maxWidth: '400px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ marginBottom: '20px' }}>
                                            <div style={{
                                                fontSize: '48px',
                                                color: '#ff9800',
                                                marginBottom: '15px'
                                            }}>
                                                ⚠️
                                            </div>
                                            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>
                                                {deleteModal.type === 'table' ? 'Delete Specification Table' : 'Delete Specification'}
                                            </h3>
                                            <p style={{ margin: '0', color: '#666', lineHeight: '1.5' }}>
                                                {deleteModal.type === 'table' ? (
                                                    <>
                                                        Are you sure you want to delete <strong>Specification Product {deleteModal.tableIndex + 1}</strong>?
                                                        <br />
                                                        <span style={{ color: '#d32f2f', fontSize: '14px' }}>
                                                            This will delete all specifications in this table.
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        Are you sure you want to delete this specification from <strong>Product {deleteModal.tableIndex + 1}</strong>?
                                                        <br />
                                                        <span style={{ color: '#d32f2f', fontSize: '14px' }}>
                                                            This action cannot be undone.
                                                        </span>
                                                    </>
                                                )}
                                            </p>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                            <button
                                                type="button"
                                                onClick={cancelDelete}
                                                style={{
                                                    padding: '10px 20px',
                                                    background: '#f5f5f5',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={confirmDelete}
                                                style={{
                                                    padding: '10px 20px',
                                                    background: '#d32f2f',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {deleteModal.type === 'table' ? 'Delete Table' : 'Delete Specification'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
